import React, { useState, useRef, useEffect } from 'react';
import s from './ToolsButtons.module.css';
import IconDownload from '../../../../shared/ui/icons/IconDownload';
import IconDiamond from '../../../../shared/ui/icons/IconDiamond';
import {
    selectSelectedImage,
    selectImageProject,
    selectImageLayout,
    selectLoadingDownload,
} from '../../../../store/selectors/imageSelectors';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import {
    downloadImage,
    downloadAllImages,
    downloadLayoutImage,
    updateLayoutImage,
    uploadImage,
} from '../../../../shared/api/imageProjectApi';
import { useCanvasContainer } from '../../../../shared/context/CanvasContainerContext';
import { toPng } from 'html-to-image';
import { toast } from 'react-toastify';
import { selectActivePrompt } from '../../../../store/selectors/pageSelectors';
import { uploadCanvasImage } from '../../../../shared/helpers/downloadCanvas';
import { setLoadingDownload } from '../../../../store/slices/imageSlice';
interface DownloadButtonProps {
    isActive?: boolean;
    onClick?: () => void;
}

const DownloadButton: React.FC<DownloadButtonProps> = ({ isActive, onClick }) => {
    const selectedImage = useSelector(selectSelectedImage);
    const imageProject = useSelector(selectImageProject);
    const activeLayout = useSelector(selectImageLayout);
    const activePrompt = useSelector(selectActivePrompt);

    const [menuOpen, setMenuOpen] = useState(false);
    const [hoveredTarget, setHoveredTarget] = useState<'selected' | 'all' | 'frame' | null>(null);
    const [hoveredWatermark, setHoveredWatermark] = useState<'with' | 'without' | null>(null);

    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const dispatch = useAppDispatch();
    const canvas = useCanvasContainer();

    const currentLayout = imageProject?.layouts?.find((l) => l.id === activeLayout?.id);
    const isFullScreen = activePrompt === ('fullScreen' as any);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(e.target as Node) &&
                !buttonRef.current?.contains(e.target as Node)
            ) {
                setMenuOpen(false);
                setHoveredTarget(null);
                setHoveredWatermark(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleMenu = () => {
        if (onClick) onClick();
        setMenuOpen((prev) => !prev);
        if (!menuOpen) {
            setHoveredTarget(null);
            setHoveredWatermark(null);
        }
    };

    const handleDownload = async (
        target: 'selected' | 'all' | 'frame' | null,
        watermark: boolean,
        format: 'SVG' | 'PNG' | 'JPG',
    ) => {
        console.log(
            `Downloading ${
                target ?? 'slot'
            } ${watermark ? 'with' : 'without'} watermark as ${format}`,
        );
        setMenuOpen(false);
        setHoveredTarget(null);
        setHoveredWatermark(null);

        const imageFormat = format.toLowerCase() as 'svg' | 'png' | 'jpg';
        dispatch(setLoadingDownload(true));
        if (target === 'frame') {
            if (canvas.current && activeLayout?.id) {
                const canvasEl = canvas.current.querySelector('canvas') as HTMLCanvasElement;
                if (canvasEl) {
                    const file = await uploadCanvasImage(canvas);
                    if (file) {
                        await dispatch(
                            updateLayoutImage({ layoutId: activeLayout.id, file }),
                        ).unwrap();
                    }
                }
            }
        }

        try {
            if (target === 'selected' && selectedImage.image?.id) {
                console.log('watermark', watermark);
                await dispatch(
                    downloadImage({
                        imageId: selectedImage.image.id,
                        imageFormat,
                        watermarked: watermark,
                    }),
                ).unwrap();
            } else if (target === 'all' && currentLayout) {
                await dispatch(
                    downloadAllImages({
                        layoutId: currentLayout.id,
                        imageFormat,
                        watermarked: watermark,
                    }),
                ).unwrap();
            } else if (target === 'frame' && currentLayout) {
                await dispatch(
                    downloadLayoutImage({
                        layoutId: currentLayout.id,
                        watermark: watermark,
                        ext: imageFormat,
                    }),
                ).unwrap();
            } else if (target === null && isFullScreen && selectedImage.image?.id) {
                await dispatch(
                    downloadImage({
                        imageId: selectedImage.image.id,
                        imageFormat,
                        watermarked: watermark,
                    }),
                ).unwrap();
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to download');
            console.error('Failed to download:', error);
        } finally {
            dispatch(setLoadingDownload(false));
        }
    };

    return (
        <div className={s.download_wrapper}>
            <button
                ref={buttonRef}
                className={`${s.container} ${isActive ? s.active : ''}`}
                onClick={toggleMenu}
            >
                <IconDownload />
                <p style={{ position: 'relative', left: -8 }}>Download</p>
            </button>

            {menuOpen && (
                <div ref={menuRef} className={s.download_menu}>
                    {isFullScreen ? (
                        <>
                            <div
                                className={s.download_menu_item}
                                onMouseEnter={() => setHoveredWatermark('with')}
                            >
                                With watermark <span className={s.download_arrow}>▶</span>
                            </div>
                            <div
                                className={s.download_menu_item}
                                onMouseEnter={() => setHoveredWatermark('without')}
                            >
                                Without watermark <span className={s.download_arrow}>▶</span>
                            </div>
                            {hoveredWatermark && (
                                <div
                                    className={s.download_submenu}
                                    style={{ width: '60px', left: '-70px' }}
                                >
                                    <div
                                        className={`${s.download_menu_item} ${s.download_disabled}`}
                                        onClick={() =>
                                            handleDownload(null, hoveredWatermark === 'with', 'SVG')
                                        }
                                    >
                                        SVG <IconDiamond width={10} />
                                    </div>
                                    <div
                                        className={s.download_menu_item}
                                        onClick={() =>
                                            handleDownload(null, hoveredWatermark === 'with', 'JPG')
                                        }
                                    >
                                        JPG
                                    </div>
                                    <div
                                        className={s.download_menu_item}
                                        onClick={() =>
                                            handleDownload(null, hoveredWatermark === 'with', 'PNG')
                                        }
                                    >
                                        PNG
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            {selectedImage?.image && (
                                <div
                                    className={s.download_menu_item}
                                    onMouseEnter={() => {
                                        setHoveredTarget('selected');
                                        setHoveredWatermark(null);
                                    }}
                                >
                                    Selected <span className={s.download_arrow}>▶</span>
                                </div>
                            )}
                            <div
                                className={s.download_menu_item}
                                onMouseEnter={() => {
                                    setHoveredTarget('all');
                                    setHoveredWatermark(null);
                                }}
                            >
                                All images <span className={s.download_arrow}>▶</span>
                            </div>
                            <div
                                className={s.download_menu_item}
                                onMouseEnter={() => {
                                    setHoveredTarget('frame');
                                    setHoveredWatermark(null);
                                }}
                            >
                                Frame <span className={s.download_arrow}>▶</span>
                            </div>

                            {hoveredTarget && (
                                <div className={s.download_submenu}>
                                    <div
                                        className={s.download_menu_item}
                                        onMouseEnter={() => setHoveredWatermark('with')}
                                    >
                                        With watermark <span className={s.download_arrow}>▶</span>
                                    </div>
                                    <div
                                        className={s.download_menu_item}
                                        onMouseEnter={() => setHoveredWatermark('without')}
                                    >
                                        Without watermark{' '}
                                        <span className={s.download_arrow}>▶</span>
                                    </div>
                                </div>
                            )}

                            {hoveredWatermark && hoveredTarget && (
                                <div className={`${s.download_submenu_right}`}>
                                    <div
                                        className={`${s.download_menu_item} ${s.download_disabled}`}
                                        onClick={() =>
                                            handleDownload(
                                                hoveredTarget,
                                                hoveredWatermark === 'with',
                                                'SVG',
                                            )
                                        }
                                    >
                                        SVG <IconDiamond width={10} />
                                    </div>
                                    <div
                                        className={s.download_menu_item}
                                        onClick={() =>
                                            handleDownload(
                                                hoveredTarget,
                                                hoveredWatermark === 'with',
                                                'JPG',
                                            )
                                        }
                                    >
                                        JPG
                                    </div>
                                    <div
                                        className={s.download_menu_item}
                                        onClick={() =>
                                            handleDownload(
                                                hoveredTarget,
                                                hoveredWatermark === 'with',
                                                'PNG',
                                            )
                                        }
                                    >
                                        PNG
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default DownloadButton;
